import React, { useMemo, useState, useEffect, useContext, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import DataTable from "../../../components/common/DataTable";
import { READ_SUPPLIER_LEDGER } from "../../../services/apiRoutes";
import { AuthContext } from "../../../context/AuthContext";
import CustomLoader from "../../common/CustomLoader";
import CustomReport from "../../common/CustomReport";
import { genericPost } from "../../../services/authServices";
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomDataTable from "../../common/CustomDataTable";
import CustomFilter from "../../common/CustomFilter";
import { Box, Grid } from "@mui/material";
import CustomTextField from "../../common/CustomTextField";
import CustomButton from "../../common/CustomButton";



const title = "Supplier Ledger Wise Invoice";

const SupplierledgerView = () => {
    const { clientCompanyId, currencyId } = useParams();
    const companyDateFormat = useCompanyDateFormat();

    const { t, i18n } = useTranslation();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [clientCompanyName, setClientCompanyName] = useState("Customer Ledger");

    const [totalAmount, setTotalAmount] = useState("");
    const [totalPaymentAmount, setTotalPaymentAmount] = useState("");
    const [totalBalance, setTotalBalance] = useState("");
    const [currencySymbol, setCurrencySymbol] = useState("");
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    // ✅ Pagination states
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [filterPayload, setFilterPayload] = useState({
        shipmentId: '',
        fromDate: '',
        toDate: '',
    });
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useCallback(() => {
        setFilterPayload({
            shipmentId: "",
            fromDate: '',
            toDate: '',
        });
        setPage(0);
    }, []);



    const columns = useMemo(
        () => [
            { accessorKey: 'serialNo', header: 'SN', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'date', header: 'Date', size: 100, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment I’d', size: 150, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'commercialInvoiceNo', header: 'Commercial Invoice', size: 100, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: 'productName', header: 'Particular', size: 150, tableHeader: {
                    justifyContent: 'flex-start',
                }, footer: 'Total',
            },
            {
                accessorKey: 'invoiceAmount', header: 'Invoice Amount', size: 100, tableStyle: { textAlign: "center" }, footer: `${currencySymbol} ${totalAmount ? totalAmount.toFixed(2) : '0.00'}`
            },
            {
                accessorKey: 'paymentAmount', header: 'Payment', size: 100, tableStyle: { textAlign: "center" }, footer: `${currencySymbol} ${totalPaymentAmount ? totalPaymentAmount.toFixed(2) : '0.00'}`
            },
            {
                accessorKey: 'balance', header: 'Balance', size: 100, tableStyle: { textAlign: "center" }, footer: `${currencySymbol} ${totalBalance ? totalBalance.toFixed(2) : '0.00'}`
            },
        ],
        [totalAmount, totalPaymentAmount, totalBalance, currencySymbol]
    );

    const fetchData = async (
        clientCompanyId,
        currencyId,
        pageIndex = page,
        pageLimit = pageSize
    ) => {

        if (!pageLimit || pageLimit <= 0) pageLimit = 10;
        if (!pageIndex || pageIndex < 0) pageIndex = 0;
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageLimit,
                clientCompanyId,
                currencyId,
                shipmentId: filterPayload.shipmentId || null,
                fromDate: filterPayload.fromDate || null,
                toDate: filterPayload.toDate || null,
            };
            const response = await genericPost(READ_SUPPLIER_LEDGER, payload);
            if (!response?.data?.success) {
                setData([]);
                return;
            }
            const {
                data: ledgerData,
                totalGrandAmount,
                totalGrandPayment,
                totalBalanceAmount,
                pagination
            } = response.data;

            // ---------------------------
            // 🟦 MAP ROW DATA
            // ---------------------------
            const mappedRows = ledgerData.map((item, index) => {
                const currencySymbol = item.currency?.currency_symbol || "";
                // sum particulars amount
                const totalAmount = item.particulars?.reduce(
                    (sum, p) => sum + parseFloat(p.amount || 0),
                    0
                );
                // sum payment referenceAmount
                const paymentAmount = item.PaymentVoucherParticularsList?.reduce(
                    (sum, pv) => sum + parseFloat(pv.referenceAmount || 0),
                    0
                );
                const balanceAmount = totalAmount - paymentAmount;
                const productNames = item.particulars
                    ?.map(p => p.products?.productName)
                    ?.filter(Boolean)
                    .join(", ") || "";
                return {
                    serialNo: String(index + 1).padStart(2, "0"),
                    date: companyDateFormat(item.date),
                    shipmentId: item.shipmentId,
                    commercialInvoiceNo: item.commercialInvoiceNo,
                    productName: productNames,
                    invoiceAmount: `${currencySymbol} ${totalAmount.toFixed(2)}`,
                    paymentAmount: `${currencySymbol} ${paymentAmount.toFixed(2)}`,
                    balance: `${currencySymbol} ${balanceAmount.toFixed(2)}`,
                    rawTotalAmount: totalAmount,
                    rawPayment: paymentAmount,
                    rawBalance: balanceAmount,
                };
            });
            // ---------------------------
            // 🟦 SET STATE
            // ---------------------------
            setData(mappedRows);
            setTotalAmount(parseFloat(totalGrandAmount || 0));
            setTotalPaymentAmount(parseFloat(totalGrandPayment || 0));
            setTotalBalance(parseFloat(totalBalanceAmount || 0));
            setRowCount(pagination?.totalRecords || 0);
            if (ledgerData.length > 0) {
                const symbol = ledgerData[0]?.currency?.currency_symbol || "";
                setCurrencySymbol(symbol);
            }
        } catch (err) {
            console.error("Error:", err);
            setError(err.message);
            setData([]);
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        if (!clientCompanyId || !currencyId) return;
        fetchData(clientCompanyId, currencyId);
    }, [clientCompanyId, currencyId, token, page, pageSize, filterPayload.shipmentId, filterPayload.fromDate, filterPayload.toDate,]);


    const defaultView = 'supplierledger';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={clientCompanyName}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}

            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );

    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={mainContent} />
        </>
    );
};

export default SupplierledgerView;
